import * as React from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Circle, ArrowRight } from "lucide-react";

interface GuideStep {
  title: string;
  description: string;
  completed?: boolean;
}

interface EmptyStateProps {
  title?: string;
  description?: string;
  action?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  icon?: React.ReactNode;
  tips?: string[];
  steps?: GuideStep[];
  illustration?: React.ReactNode;
}

export function EmptyState({
  title,
  description,
  action,
  secondaryAction,
  icon,
  tips,
  steps,
  illustration,
}: EmptyStateProps) {
  const { t } = useTranslation("common");
  const resolvedTitle =
    title ?? t("emptyState.defaultTitle", "Nothing here yet");

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-card rounded-xl border border-dashed border-primary/20 p-8 text-center shadow-sm"
    >
      <div className="mx-auto max-w-2xl">
        {illustration ? (
          <div className="mb-6 flex justify-center">{illustration}</div>
        ) : icon ? (
          <div className="mb-4 flex justify-center text-primary/60">{icon}</div>
        ) : null}

        <h3 className="text-xl font-semibold tracking-tight">
          {resolvedTitle}
        </h3>
        {description && (
          <p className="mt-2 text-sm text-muted-foreground mx-auto max-w-md">
            {description}
          </p>
        )}

        {steps && steps.length > 0 && (
          <div className="mt-8 grid gap-4 text-left sm:grid-cols-1 md:grid-cols-3">
            {steps.map((step, i) => (
              <div
                key={i}
                className="group relative rounded-lg border bg-background/50 p-4 transition-all hover:border-primary/50 hover:bg-background"
              >
                <div className="mb-2 flex items-center justify-between">
                  {step.completed ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground/30 group-hover:text-primary/50" />
                  )}
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/50">
                    {t("emptyState.step", {
                      number: i + 1,
                      defaultValue: `Step ${i + 1}`,
                    })}
                  </span>
                </div>
                <div className="text-sm font-semibold">{step.title}</div>
                <div className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  {step.description}
                </div>
              </div>
            ))}
          </div>
        )}

        {(action || secondaryAction) && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {action}
            {secondaryAction}
          </div>
        )}

        {tips && tips.length > 0 && (
          <div className="mt-8 rounded-lg bg-muted/30 p-4">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground text-left mb-2 px-1">
              {t("emptyState.proTips", "Pro Tips")}
            </h4>
            <ul className="grid gap-2 text-left text-xs text-muted-foreground sm:grid-cols-2">
              {tips.map((tText, i) => (
                <li key={i} className="flex items-start gap-2">
                  <ArrowRight className="mt-0.5 h-3 w-3 shrink-0 text-primary/40" />
                  <span>{tText}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
